import type { RichText } from "@storytell/cp-client";
import {
  type PmBlockquote,
  type PmBulletList,
  type PmCodeBlock,
  type PmDoc,
  type PmHeading,
  type PmHorizontalRule,
  type PmImage,
  type PmInline,
  type PmListItem,
  type PmMark,
  PmMarkType,
  type PmNode,
  PmNodeType,
  type PmOrderedList,
  type PmParagraph,
  type PmTable,
  type PmTableCell,
  type PmTableHeader,
  type PmTemplateVar,
  type PmText,
  RichTextType,
} from "@storytell/types/richText";

// Custom type for template variables (JS-side only, not in gRPC)
export const TemplateVarRichTextType = "template_var:v1" as const;

// Custom type for pasted text chips (JS-side only, not in gRPC)
export const PastedTextRichTextType = "pasted_text:v1" as const;

interface ProseMirrorToRichTextOptions {
  /**
   * When true, template variables are preserved in their original structure.
   * When false (default), template variables are resolved to their values for submission.
   */
  preserveTemplateVars?: boolean;
}

const parseMark = (mark: PmMark): RichText.Mark | undefined => {
  switch (mark.type) {
    case PmMarkType.Bold:
      return { type: RichTextType.BoldType } as RichText.Mark;
    case PmMarkType.Italic:
      return { type: RichTextType.ItalicType } as RichText.Mark;
    case PmMarkType.Strike:
      return { type: RichTextType.StrikeType } as RichText.Mark;
    case PmMarkType.Code:
      return { type: RichTextType.CodeType } as RichText.Mark;
    case PmMarkType.Link:
      return {
        type: RichTextType.LinkType,
        attrs: {
          case: "linkAttrs",
          value: { href: mark.attrs.href, title: "" },
        },
      } as RichText.Mark;
  }
};

const parseText = (node: PmText): RichText.Node =>
  ({
    type: RichTextType.TextType,
    text: node.text,
    marks: (node.marks?.map(parseMark).filter(Boolean) as RichText.Mark[]) || [],
  }) as RichText.Node;

/**
 * Parses a template variable node, preserving its full structure.
 * The template var attrs are stored in the RichText node so they can be restored when editing.
 */
const preserveTemplateVar = (node: PmTemplateVar): RichText.Node | undefined => {
  const { varType, name, options, value, required, fallback } = node.attrs;

  return {
    type: TemplateVarRichTextType,
    attrs: {
      case: "templateVarAttrs",
      value: {
        varType,
        name,
        options,
        value,
        required,
        fallback,
      },
    },
  } as unknown as RichText.Node;
};

/**
 * Resolves a template variable to its filled value for submission.
 * - text/dropdown vars → text node with the value (or fallback when optional and empty)
 * - file-pick vars → variable node (asset mention), or fallback text when optional and empty
 * - Empty required vars return undefined (filtered out)
 */
const resolveTemplateVar = (node: PmTemplateVar): RichText.Node | undefined => {
  const { varType, value, required, fallback } = node.attrs;
  const isOptional = required === false;
  const fallbackText = fallback != null && fallback !== "" ? fallback : "";

  const emptyToFallback = (): RichText.Node | undefined =>
    isOptional
      ? ({ type: RichTextType.TextType, text: fallbackText, marks: [] } as unknown as RichText.Node)
      : undefined;

  switch (varType) {
    case "text":
    case "paragraph": {
      const textValue = (value as { text: string }).text || "";
      if (!textValue.trim()) return emptyToFallback();
      return { type: RichTextType.TextType, text: textValue, marks: [] } as unknown as RichText.Node;
    }
    case "dropdown": {
      const dropdownValue = (value as { value: string }).value || "";
      if (!dropdownValue) return emptyToFallback();
      return { type: RichTextType.TextType, text: dropdownValue, marks: [] } as unknown as RichText.Node;
    }
    case "file-pick":
    case "record-audio": {
      const fileValue = value as { assetId: string; filename?: string };
      if (!fileValue.assetId) return emptyToFallback();
      return {
        type: RichTextType.MustacheVariableType,
        attrs: {
          case: "variableAttrs",
          value: { value: fileValue.assetId, text: fileValue.filename || "", type: "asset" },
        },
      } as RichText.Node;
    }
    default:
      return undefined;
  }
};

const parseTemplateVar = (node: PmTemplateVar, options: ProseMirrorToRichTextOptions): RichText.Node | undefined => {
  if (options.preserveTemplateVars) {
    return preserveTemplateVar(node);
  }
  return resolveTemplateVar(node);
};

const parseVariable = (node: PmNode) => {
  if (node.type !== PmNodeType.VariableV1) return undefined;

  const data = (node.attrs as { data?: { displayName?: string; name?: string } }).data;
  const displayText = data?.displayName || node.attrs.text;

  return {
    type: RichTextType.MustacheVariableType,
    attrs: {
      case: "variableAttrs",
      value: {
        value: node.attrs.id,
        text: displayText,
        type: node.attrs.type,
        name: data?.name,
      },
    },
  } as RichText.Node;
};

const parseInlineNodes = (
  content: PmInline[] | undefined,
  options: ProseMirrorToRichTextOptions
): RichText.Node[] | undefined =>
  content
    ?.map((node) => {
      switch (node.type) {
        case PmNodeType.Text:
          return parseText(node);
        case PmNodeType.VariableV1:
          return parseVariable(node);
        case PmNodeType.TemplateVarV1:
          return parseTemplateVar(node, options);
        case PmNodeType.PastedTextV1:
          return {
            type: PastedTextRichTextType,
            text: node.attrs.text,
            marks: [],
          } as unknown as RichText.Node;
        case PmNodeType.HardBreak:
          return { type: "hardBreak" } as unknown as RichText.Node;
        default:
          return undefined;
      }
    })
    .filter(Boolean) as RichText.Node[];

const parseParagraph = (node: PmParagraph, options: ProseMirrorToRichTextOptions): RichText.Node =>
  ({
    type: RichTextType.ParagraphType,
    content: parseInlineNodes(node.content, options),
  }) as RichText.Node;

const parseHeading = (node: PmHeading, options: ProseMirrorToRichTextOptions): RichText.Node =>
  ({
    type: RichTextType.HeadingType,
    attrs: {
      case: "headingAttrs",
      value: { level: node.attrs.level },
    },
    content: parseInlineNodes(node.content, options),
  }) as RichText.Node;

const parseListItem = (node: PmListItem, options: ProseMirrorToRichTextOptions): RichText.Node =>
  ({
    type: RichTextType.ListItemType,
    attrs: {
      case: "listItemAttrs",
      value: {
        level: 1,
        checked: undefined, // TODO: Extract from task list structure if needed
      },
    },
    content: parseBlockNodes(node.content, options),
  }) as unknown as RichText.Node;

const parseList = (node: PmBulletList | PmOrderedList, options: ProseMirrorToRichTextOptions): RichText.Node => {
  const listType = node.type === PmNodeType.OrderedList ? "ordered" : "bullet";
  return {
    type: RichTextType.ListType,
    attrs: {
      case: "listAttrs",
      value: {
        listType,
        start: listType === "ordered" ? 1 : undefined,
      },
    },
    content: node.content.map((item) => parseListItem(item, options)),
  } as RichText.Node;
};

const parseBlockquote = (node: PmBlockquote, options: ProseMirrorToRichTextOptions): RichText.Node =>
  ({
    type: RichTextType.BlockquoteType,
    content: parseBlockNodes(node.content, options),
  }) as RichText.Node;

const parseCodeBlock = (node: PmCodeBlock): RichText.Node =>
  ({
    type: RichTextType.CodeBlockType,
    attrs: {
      case: "codeBlockAttrs",
      value: { language: node.attrs?.language || "" },
    },
    content: node.content?.map(parseText),
  }) as RichText.Node;

const parseTableCell = (
  node: PmTableCell | PmTableHeader,
  options: ProseMirrorToRichTextOptions,
  cellOptions?: { forceCellType?: "tableCell" }
): RichText.Node => {
  const isHeader = node.type === PmNodeType.TableHeader && cellOptions?.forceCellType !== "tableCell";
  return {
    type: isHeader ? RichTextType.TableHeaderType : RichTextType.TableCellType,
    attrs: node.attrs
      ? {
          case: "tableCellAttrs",
          value: {
            colspan: node.attrs.colspan || 1,
            rowspan: node.attrs.rowspan || 1,
            alignment: node.attrs.alignment || "left",
          },
        }
      : undefined,
    content: node.content
      .flatMap((contentNode) => {
        // If it's a paragraph, extract its inline content
        if (contentNode.type === PmNodeType.Paragraph) {
          return parseInlineNodes(contentNode.content, options);
        }
        // Handle other content types
        const blockNodes = parseBlockNodes([contentNode], options);
        return blockNodes.length > 0 ? blockNodes[0] : undefined;
      })
      .filter(Boolean) as RichText.Node[],
  } as RichText.Node;
};

const parseTable = (node: PmTable, options: ProseMirrorToRichTextOptions): RichText.Node => {
  // Count rows and columns
  const rows = node.content.length;
  const cols = rows > 0 && node.content[0] ? node.content[0].content.length : 0;

  // Check if first row contains header cells
  const hasHeaderRow = rows > 0 && node.content[0]?.content.every((cell) => cell.type === PmNodeType.TableHeader);

  // Convert table rows to the correct structure
  const convertedContent: RichText.Node[] = [];

  for (let i = 0; i < node.content.length; i++) {
    const row = node.content[i];
    if (!row) continue;

    if (i === 0 && hasHeaderRow) {
      // First row with header cells should be tableHeader, but children should be tableCell
      const headerCells: RichText.Node[] = [];
      for (const cell of row.content as (PmTableCell | PmTableHeader)[]) {
        headerCells.push(parseTableCell(cell, options, { forceCellType: "tableCell" }));
      }
      convertedContent.push({
        type: RichTextType.TableHeaderType,
        content: headerCells,
      } as RichText.Node);
    } else {
      // Regular rows should be tableRow
      const rowCells: RichText.Node[] = [];
      for (const cell of row.content as (PmTableCell | PmTableHeader)[]) {
        rowCells.push(parseTableCell(cell, options));
      }
      convertedContent.push({
        type: RichTextType.TableRowType,
        content: rowCells,
      } as RichText.Node);
    }
  }

  return {
    type: RichTextType.TableType,
    attrs: {
      case: "tableAttrs",
      value: {
        rows,
        cols,
        headerRow: hasHeaderRow,
      },
    },
    content: convertedContent,
  } as RichText.Node;
};

const parseHorizontalRule = (node: PmHorizontalRule): RichText.Node =>
  ({
    type: RichTextType.HorizontalRuleType,
    attrs: node.attrs
      ? {
          case: "hrAttrs",
          value: { style: node.attrs.style || "solid" },
        }
      : undefined,
  }) as RichText.Node;

const parseImage = (node: PmImage): RichText.Node =>
  ({
    type: RichTextType.ImageType,
    attrs: {
      case: "imageAttrs",
      value: {
        src: node.attrs.src,
        alt: node.attrs.alt || "",
        title: node.attrs.title || "",
      },
    },
  }) as RichText.Node;

const parseBlockNodes = (content: PmNode[], options: ProseMirrorToRichTextOptions): RichText.Node[] =>
  content
    .map((node) => {
      switch (node.type) {
        case PmNodeType.VariableV1:
          return parseVariable(node);
        case PmNodeType.Paragraph:
          return parseParagraph(node, options);
        case PmNodeType.Heading:
          return parseHeading(node, options);
        case PmNodeType.BulletList:
        case PmNodeType.OrderedList:
          return parseList(node, options);
        case PmNodeType.Blockquote:
          return parseBlockquote(node, options);
        case PmNodeType.CodeBlock:
          return parseCodeBlock(node);
        case PmNodeType.Table:
          return parseTable(node, options);
        case PmNodeType.HorizontalRule:
          return parseHorizontalRule(node);
        case PmNodeType.Image:
          return parseImage(node);
        default:
          return undefined;
      }
    })
    .filter(Boolean) as RichText.Node[];

export const proseMirrorToRichText = (pmDoc: PmDoc, options: ProseMirrorToRichTextOptions = {}): RichText.RichText =>
  ({
    type: RichTextType.DocumentType,
    content: parseBlockNodes(pmDoc.content, options),
  }) as RichText.RichText;
